import React from 'react';
import type { ApiCatalogDetail } from '@chatsdet/types';
import { Button, useToast } from '@chatsdet/ui';
import { Lightning } from '@phosphor-icons/react';
import { useUpdateApiRequest } from '../api';
import { QueryParamSchemaEditor } from '../../../ui/components/api-catalog/QueryParamSchemaEditor';
import { formatJsonDisplay } from '../../../ui/components/api-catalog/JsonBlock';
import { CodePanel, type CodeTab } from '../../../ui/components/api-catalog/CodePanel';
import { DrawerSectionLabel, HeadersTable } from '../../../ui/components/api-catalog/DrawerTables';

/** Infer a JSON-Schema-like type descriptor from a JS value. */
function inferSchema(value: unknown): unknown {
  if (value === null || value === undefined) return { type: 'null' };
  if (Array.isArray(value)) {
    if (value.length === 0) return { type: 'array', items: {} };
    return { type: 'array', items: inferSchema(value[0]) };
  }
  if (typeof value === 'object') {
    const properties: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      properties[k] = inferSchema(v);
    }
    return { type: 'object', properties };
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? { type: 'integer' } : { type: 'number' };
  }
  if (typeof value === 'boolean') return { type: 'boolean' };
  return { type: 'string' };
}

/** Count of top-level properties in a JSON-schema-ish object (for the tab badge). */
function schemaPropCount(schema: unknown): number | null {
  if (schema && typeof schema === 'object' && 'properties' in schema) {
    const props = (schema as { properties?: Record<string, unknown> }).properties;
    if (props && typeof props === 'object') return Object.keys(props).length;
  }
  return null;
}

/** Build a runnable cURL from the real method/url/headers/body. */
function buildCurl(
  method: string,
  url: string | null,
  headers: Record<string, string> | null | undefined,
  body: string | null
): string | null {
  if (!url) return null;
  const parts = [`curl -X ${method} '${url}'`];
  if (headers) {
    for (const [k, v] of Object.entries(headers)) parts.push(`  -H '${k}: ${v}'`);
  }
  if (body && method.toUpperCase() !== 'GET') {
    parts.push(`  -d '${body.replace(/\n/g, '')}'`);
  }
  return parts.join(' \\\n');
}

type RequestTabPanelProps = {
  detail: ApiCatalogDetail;
};

export function RequestTabPanel({ detail }: RequestTabPanelProps) {
  const { toast } = useToast();
  const updateMutation = useUpdateApiRequest();

  const handleSaveSchema = React.useCallback(
    (value: string | null) => {
      let parsed: unknown = null;
      if (value) {
        try {
          parsed = JSON.parse(value);
        } catch {
          toast.error('Schema is not valid JSON');
          return;
        }
      }
      updateMutation.mutate(
        { requestId: detail.id, requestSchema: parsed },
        {
          onSuccess: () => toast.success('Body schema saved'),
          onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to save'),
        }
      );
    },
    [detail.id, updateMutation, toast]
  );

  const handleSaveSample = React.useCallback(
    (value: string | null) => {
      updateMutation.mutate(
        { requestId: detail.id, requestSample: value },
        {
          onSuccess: () => toast.success('Sample saved'),
          onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to save'),
        }
      );
    },
    [detail.id, updateMutation, toast]
  );

  const schemaText = formatJsonDisplay(detail.requestSchema);
  const sampleText = formatJsonDisplay(detail.requestSample);
  const requestHeaders = detail.latestOccurrence?.requestHeaders ?? null;
  const curlText = buildCurl(detail.method, detail.exampleUrl, requestHeaders, detail.requestSample);
  const propCount = schemaPropCount(detail.requestSchema);

  const handleGenerateFromSample = React.useCallback(() => {
    if (!sampleText) return;
    try {
      const parsed = JSON.parse(sampleText);
      updateMutation.mutate(
        { requestId: detail.id, requestSchema: inferSchema(parsed) },
        {
          onSuccess: () => toast.success('Schema generated from sample'),
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Failed to generate schema'),
        }
      );
    } catch {
      toast.error('Sample is not valid JSON — cannot generate schema');
    }
  }, [sampleText, detail.id, updateMutation, toast]);

  const bodyTabs: CodeTab[] = [
    {
      id: 'schema',
      label: 'Schema',
      badge: propCount ? `${propCount} props` : undefined,
      content: schemaText,
      language: 'json',
      onSave: handleSaveSchema,
      emptyText: 'No request body schema inferred yet.',
    },
    {
      id: 'sample',
      label: 'Sample',
      content: sampleText,
      language: 'json',
      onSave: handleSaveSample,
      emptyText: 'No request sample captured.',
    },
    ...(curlText
      ? [{ id: 'curl', label: 'cURL', content: curlText, language: 'bash' } as CodeTab]
      : []),
  ];

  return (
    <div className="px-6 pt-4 pb-6 space-y-6">
      {/* Query / path parameters (editable) */}
      <QueryParamSchemaEditor requestId={detail.id} schema={detail.queryParamSchema} />

      {/* Request headers — from the most recent observed call */}
      <div>
        <div className="mb-2.5">
          <DrawerSectionLabel
            count={requestHeaders ? Object.keys(requestHeaders).length : undefined}
          >
            Request headers
          </DrawerSectionLabel>
        </div>
        <HeadersTable
          headers={requestHeaders}
          emptyText="No request headers captured on the latest call."
        />
      </div>

      {/* Body — segmented code viewer */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <DrawerSectionLabel>Body</DrawerSectionLabel>
          {sampleText && (
            <Button
              variant="ghost"
              size="sm"
              onPress={handleGenerateFromSample}
              isDisabled={updateMutation.isPending}
              iconLeft={<Lightning size={14} />}
              tooltip="Infer a JSON schema from the sample request"
            >
              Generate from sample
            </Button>
          )}
        </div>
        <CodePanel
          tabs={bodyTabs}
          isSaving={updateMutation.isPending}
          contentTypeLabel="application/json"
        />
      </div>
    </div>
  );
}
